import { Button } from "@/components/ui/button";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import { useExpand } from "@/hooks/use-expand";
import { useHistory } from "@/providers/history.provider";
import { BatchStatus } from "@/types/dashboard.types";
import { Link } from "@tanstack/react-router";
import { Bell, Loader2, PackageOpen } from "lucide-react";
import { toast } from "sonner";

export function Notifications() {
  const { history, isPending } = useHistory();

  const { isExpanded, setIsExpanded } = useExpand();

  const batches = history?.pages.flatMap((page) => page.batches);

  const notifications = batches?.filter(
    (batch) => batch.status === "REVIEW_REQUIRED" || batch.status === "CREATED"
  );

  const total = notifications?.length ? notifications.length : 0;

  return (
    <div>
      {isPending ? (
        <Skeleton className="size-10" />
      ) : (
        <DropdownMenu open={isExpanded} onOpenChange={setIsExpanded}>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="relative" size="icon">
              <Bell />
              {total > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full text-xs w-4 h-4 flex items-center justify-center">
                  {total}
                </span>
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-96">
            <DropdownMenuLabel>Notifications</DropdownMenuLabel>
            <DropdownMenuGroup>
              {notifications && notifications.length === 0 && (
                <span>
                  <DropdownMenuItem className="text-muted-foreground">
                    Nothing to see here
                    <PackageOpen />
                  </DropdownMenuItem>
                </span>
              )}

              {notifications &&
                notifications?.map((batch, idx) => (
                  <Link
                    to={`/dashboard/conversion-history/$id`}
                    params={{ id: batch.id }}
                    disabled={batch.status === "CREATED"}
                    onClick={(e) => {
                      if (batch.status === "CREATED") {
                        e.preventDefault();
                        toast.warning(`File is under processing , please wait`);
                      }
                    }}
                  >
                    <DropdownMenuItem key={idx}>
                      {batch.name}
                      <DropdownMenuShortcut className="tracking-normal">
                        <StatusBadge status={batch.status} />
                      </DropdownMenuShortcut>
                    </DropdownMenuItem>
                  </Link>
                ))}
            </DropdownMenuGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: BatchStatus }) {
  if (status === "CREATED") {
    return (
      <div className="flex items-center gap-2">
        <span className="text-xs">Creating</span>
        <Loader2 className="text-blue-500 rounded-full size-4 flex items-center justify-center gap-1 animate-spin" />
      </div>
    );
  } else if (status === "REVIEW_REQUIRED") {
    return (
      <div className="flex items-center gap-2">
        <span className="text-xs">Review Required</span>
        <div className="text-yellow-500 rounded-full size-2 bg-yellow-500 flex items-center justify-center gap-1" />
      </div>
    );
  } else if (status === "FAILED") {
    return (
      <div className="flex items-center gap-2">
        <span className="text-xs">Failed</span>
        <div className="text-red-500 rounded-full size-2 bg-yellow-500 flex items-center justify-center gap-1" />
      </div>
    );
  } else {
    return null;
  }
}
